/**
 * The password policy, mirrored from the Supabase project (dashboard → Authentication, and
 * `supabase/config.toml` locally): at least 8 characters with a lowercase letter, an uppercase letter,
 * a number and a symbol. The server is the authority; this check exists so people see what's missing
 * before submitting.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_HINT =
  'At least 8 characters, with upper and lower case letters, a number and a symbol.';

export type PasswordProblem = 'length' | 'lowercase' | 'uppercase' | 'number' | 'symbol';

const label: Record<PasswordProblem, string> = {
  length: `at least ${PASSWORD_MIN_LENGTH} characters`,
  lowercase: 'a lowercase letter',
  uppercase: 'an uppercase letter',
  number: 'a number',
  symbol: 'a symbol',
};

export function passwordProblems(password: string): PasswordProblem[] {
  const problems: PasswordProblem[] = [];
  if (password.length < PASSWORD_MIN_LENGTH) problems.push('length');
  if (!/[a-z]/.test(password)) problems.push('lowercase');
  if (!/[A-Z]/.test(password)) problems.push('uppercase');
  if (!/[0-9]/.test(password)) problems.push('number');
  if (!/[^A-Za-z0-9\s]/.test(password)) problems.push('symbol');
  return problems;
}

function list(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

/** "Add an uppercase letter and a symbol." — or null when the password meets the policy. */
export function describePasswordProblems(problems: PasswordProblem[]): string | null {
  if (problems.length === 0) return null;
  return `Still needed: ${list(problems.map((p) => label[p]))}.`;
}

/** Translate Supabase's `weak_password` error (reasons + message) into a specific, plain message. */
export function weakPasswordMessage(error: {
  message?: string;
  reasons?: string[];
  weak_password?: { reasons?: string[] };
}): string {
  const reasons = error.reasons ?? error.weak_password?.reasons ?? [];
  if (reasons.includes('pwned')) {
    return 'That password has appeared in a known data breach. Choose a different one.';
  }
  const message = error.message ?? '';
  const needed: PasswordProblem[] = [];
  if (reasons.includes('length') || /at least \d+ characters/i.test(message)) needed.push('length');
  if (message.includes('abcdefghijklmnopqrstuvwxyz')) needed.push('lowercase');
  if (message.includes('ABCDEFGHIJKLMNOPQRSTUVWXYZ')) needed.push('uppercase');
  if (message.includes('0123456789')) needed.push('number');
  if (message.includes('!@#')) needed.push('symbol');
  if (needed.length === 0) return `That password doesn't meet the requirements. ${PASSWORD_HINT}`;
  return `Your password needs ${list(needed.map((p) => label[p]))}.`;
}
